import * as THREE from 'three';
import type { MaterialLibrary } from '../MaterialLibrary';

export type TowerType = 'mg' | 'cannon' | 'at';

export type TowerModel = {
  root: THREE.Group;
  turret: THREE.Group;
  barrel: THREE.Object3D | null;
  muzzleSocket: THREE.Object3D;
  range: number;
};

export class TowerFactory {
  constructor(private readonly materials: MaterialLibrary) {}

  create(type: TowerType): TowerModel {
    switch (type) {
      case 'mg':
        return this.createMG();
      case 'cannon':
        return this.createCannon();
      case 'at':
        return this.createAT();
      default:
        throw new Error(`Unknown tower type: ${type}`);
    }
  }

  private createMG(): TowerModel {
    const root = new THREE.Group();
    root.name = 'tower-mg';

    // 低矮的机枪碉堡，占地最小
    const bunker = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.5, 1.0), this.materials.concrete);
    bunker.position.y = 0.25;
    bunker.castShadow = true;
    bunker.receiveShadow = true;
    root.add(bunker);

    const roof = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.1, 1.1), this.materials.darkConcrete);
    roof.position.y = 0.55;
    roof.castShadow = true;
    roof.receiveShadow = true;
    root.add(roof);

    const slit = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.12, 0.08), this.materials.darkSteel);
    slit.position.set(0, 0.28, 0.5);
    root.add(slit);

    const turret = new THREE.Group();
    turret.position.set(0, 0.6, 0);

    const mgBase = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.18, 0.22, 10), this.materials.darkSteel);
    mgBase.position.y = 0.11;
    mgBase.castShadow = true;
    turret.add(mgBase);

    const barrelGroup = new THREE.Group();
    barrelGroup.position.y = 0.24;

    const body = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.14, 0.32), this.materials.steel);
    body.position.z = -0.08;
    body.castShadow = true;
    barrelGroup.add(body);

    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.04, 0.38, 8), this.materials.darkSteel);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0, -0.24);
    barrel.castShadow = true;
    barrelGroup.add(barrel);

    const muzzleSocket = new THREE.Object3D();
    muzzleSocket.position.set(0, 0, -0.45);
    barrelGroup.add(muzzleSocket);

    turret.add(barrelGroup);
    root.add(turret);

    return { root, turret, barrel: barrelGroup, muzzleSocket, range: 5.5 };
  }

  private createCannon(): TowerModel {
    const root = new THREE.Group();
    root.name = 'tower-cannon';

    // 圆形混凝土炮座，中等大小
    const base = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.2, 0.65, 16), this.materials.concrete);
    base.position.y = 0.325;
    base.castShadow = true;
    base.receiveShadow = true;
    root.add(base);

    const rim = new THREE.Mesh(new THREE.TorusGeometry(1.15, 0.08, 8, 24), this.materials.darkConcrete);
    rim.rotation.x = -Math.PI / 2;
    rim.position.y = 0.62;
    root.add(rim);

    const turret = new THREE.Group();
    turret.position.set(0, 0.65, 0);

    const cradle = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.45, 0.72), this.materials.steel);
    cradle.position.y = 0.23;
    cradle.castShadow = true;
    turret.add(cradle);

    const barrelGroup = new THREE.Group();
    barrelGroup.position.set(0, 0.42, 0);

    // 榴弹炮身管：短粗
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 1.25, 12), this.materials.darkSteel);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = -0.55;
    barrel.castShadow = true;
    barrelGroup.add(barrel);

    const muzzleSocket = new THREE.Object3D();
    muzzleSocket.position.set(0, 0, -1.2);
    barrelGroup.add(muzzleSocket);

    turret.add(barrelGroup);
    root.add(turret);

    return { root, turret, barrel: barrelGroup, muzzleSocket, range: 7.5 };
  }

  private createAT(): TowerModel {
    const root = new THREE.Group();
    root.name = 'tower-at';

    // 反坦克炮：开阔平台 + 大型防盾 + 最长身管
    const pad = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.3, 2.2), this.materials.darkConcrete);
    pad.position.y = 0.15;
    pad.castShadow = true;
    pad.receiveShadow = true;
    root.add(pad);

    const shield = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.9, 0.14), this.materials.steel);
    shield.position.set(0, 0.7, 0.45);
    shield.castShadow = true;
    root.add(shield);

    const turret = new THREE.Group();
    turret.position.set(0, 0.6, 0);

    const mount = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.28, 0.55), this.materials.darkSteel);
    mount.position.y = 0.14;
    mount.castShadow = true;
    turret.add(mount);

    const barrelGroup = new THREE.Group();
    barrelGroup.position.set(0, 0.32, 0);

    // 细长穿甲炮管
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.065, 1.85, 10), this.materials.steel);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = -0.85;
    barrel.castShadow = true;
    barrelGroup.add(barrel);

    const muzzleBrake = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.07, 0.18, 10), this.materials.darkSteel);
    muzzleBrake.rotation.x = Math.PI / 2;
    muzzleBrake.position.z = -1.72;
    barrelGroup.add(muzzleBrake);

    const muzzleSocket = new THREE.Object3D();
    muzzleSocket.position.set(0, 0, -1.85);
    barrelGroup.add(muzzleSocket);

    turret.add(barrelGroup);
    root.add(turret);

    return { root, turret, barrel: barrelGroup, muzzleSocket, range: 9 };
  }
}
